import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
// Stili veri del widget: l'anteprima non puo' divergere da cio' che il
// visitatore vede in chat.
import { BASE_CSS } from "../../../../widget-v2/src/styles/base-css.js";
import { widgetVarsFromTheme } from "../contacts/anteprimaModulo.js";
import { CorniceAnteprima } from "./cornice";

// Nel widget l'host copre la pagina e non riceve clic (e' il livello sopra il
// sito del cliente). Qui e' un riquadro normale dentro la dashboard.
const PREVIEW_CSS = `
  :host {
    position: relative !important;
    inset: auto !important;
    z-index: auto !important;
    pointer-events: auto !important;
    display: block !important;
    /* Sul telefono la chat riempie lo schermo anche sotto i messaggi. */
    background: var(--bg, #fff);
  }
  .puw2-preview-chat {
    display: flex;
    flex-direction: column;
    background: var(--bg, #fff);
    color: var(--text, #1f2937);
    font-family: var(--font, Inter, system-ui, sans-serif);
    font-size: var(--font-size, 14px);
  }
  .puw2-preview-chat .puw2-messages { overflow: visible; padding-bottom: 20px; }
  .puw2-preview-chat .puw2-contact-trigger-btn { cursor: default; }
`;

function Conversazione({ titolo, messaggi, sotto, children }) {
  return (
    <div className="puw2-preview-chat">
      <div className="puw2-header">
        <div>
          <h3>{titolo}</h3>
          <p>Online</p>
        </div>
      </div>
      <div className="puw2-messages">
        {messaggi.map((m, i) => (
          <div key={i} className={`puw2-msg ${m.bot ? "bot" : "user"}`}>
            <div className="puw2-bubble">
              <div className="puw2-text">{m.text}</div>
              <div className="puw2-time">ora</div>
            </div>
            {m.bot && i === messaggi.length - 1 ? children : null}
          </div>
        ))}
      </div>
      {sotto}
    </div>
  );
}

/**
 * Anteprima della chat nella pagina di un'app: un breve scambio e poi la card
 * dell'app (modulo, pulsante WhatsApp...) con i colori del widget del bot.
 * Il contenuto vive in uno shadow DOM con il CSS del widget, come in chat.
 *
 * @param {object} theme - bot.widget_config.theme
 * @param {string} titolo - nome del bot nell'intestazione
 * @param {{bot?: boolean, text: string}[]} messaggi - l'ultimo e' del bot, con la card sotto
 * @param {React.ReactNode} children - la card dell'app
 * @param {React.ReactNode} [sotto] - cio' che il widget mostra sotto i messaggi
 * @param {React.ReactNode} [piede] - note sotto l'anteprima
 */
export default function ChatPreview({ theme, titolo, messaggi, children, sotto, piede }) {
  const [dispositivo, setDispositivo] = useState("desktop");
  const hostRef = useRef(null);
  const rootRef = useRef(null);
  const varsRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });
    shadow.innerHTML = "";

    const baseStyle = document.createElement("style");
    baseStyle.textContent = BASE_CSS + PREVIEW_CSS;
    const varsStyle = document.createElement("style");
    const mount = document.createElement("div");
    shadow.append(baseStyle, varsStyle, mount);

    varsRef.current = varsStyle;
    rootRef.current = createRoot(mount);

    return () => {
      const root = rootRef.current;
      rootRef.current = null;
      // Smontare un root durante il commit di un altro fa protestare React.
      setTimeout(() => root?.unmount(), 0);
    };
  }, []);

  useEffect(() => {
    if (varsRef.current) {
      varsRef.current.textContent = `:host{${widgetVarsFromTheme(theme)}}`;
    }
    rootRef.current?.render(
      <Conversazione titolo={titolo || "Il tuo assistente"} messaggi={messaggi} sotto={sotto}>
        {children}
      </Conversazione>
    );
  });

  // Come nella pagina Widget: la chat sta sopra un sito finto. Sul computer e'
  // la finestra in basso a destra, sul telefono occupa tutto lo schermo; se
  // e' piu' alta del riquadro scorre per conto suo.
  return (
    <CorniceAnteprima
      nota={
        <>
          così la vedranno
          <br />i tuoi visitatori
        </>
      }
      dispositivo={dispositivo}
      onDispositivo={setDispositivo}
      piede={piede}
    >
      <div
        className={
          dispositivo === "desktop"
            ? "absolute bottom-4 right-4 max-h-[calc(100%-2rem)] w-[min(360px,calc(100%-2rem))] overflow-y-auto rounded-[16px] shadow-[0_12px_40px_rgba(15,23,42,0.18)] ring-1 ring-black/5"
            : "absolute inset-0 overflow-y-auto"
        }
      >
        <div ref={hostRef} className="min-h-full" />
      </div>
    </CorniceAnteprima>
  );
}
